import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { ETAPAS_VISTORIA, Stepper } from '../../ui';
import ModalConfirmacao from '../modal/confirmacao/ModalConfirmacao';
import { RootState } from '../../store/store';
import { buscarVistoriaPorId, VistoriaPorIdResponse } from '../../api/vistoria/vistoriaApi';
import {
  EstadoDaVistoria,
  situacaoDasEtapasDaVistoria,
  VistoriaNoBanco,
} from '../../utils/vistoriaFluxo';
import {
  VISTORIA_ASSINATURA,
  VISTORIA_CAPTURA,
  VISTORIA_CHECKLIST_ACESSORIOS,
  VISTORIA_LAUDO,
} from '../../views/vistoria/routes';

/**
 * As bolinhas do fluxo de vistoria, ligadas ao domínio (at-63).
 *
 * ## Por que existe
 *
 * Pedido de Victor (10/09/2026): *"as bolinhas da vistoria precisam ser
 * dinâmicas, assim como nas do fluxo de adesão"*. Isso **inverte** duas linhas do
 * `plan-d` (§4.1 "não criar `StepperVistoria`" e §4.2 "`Stepper` inerte") — e a
 * razão que criou o `StepperAdesao` vale igual aqui: são **4 telas** que precisam
 * da mesma resposta sobre o que está pendente e para onde cada bolinha leva. Sem
 * este intermediário, a regra viraria 4 cópias (convenção at-24).
 *
 * É a mesma relação que `ShellSaac` tem com o `AppShell` (`13-ui-kit.md`).
 *
 * ## 🔴 A diferença em relação ao `StepperAdesao`: a fonte é o BANCO
 *
 * O da adesão lê o **Redux**, porque é lá que aquele fluxo vive. Aqui isso daria
 * resposta errada: as fotos ficam em **estado local** da captura até o upload
 * responder, o checklist é gravado por `PATCH` e o PDF por outra tela. Perguntar
 * ao Redux pintaria de verde trabalho que o servidor não tem — o oposto do que
 * a at-24 fixou (*"estado real, lido do banco"*).
 *
 * ⚠️ Por isso o componente **faz a própria leitura** (`buscarVistoriaPorId`).
 * Custa um GET por tela; é o mesmo endpoint que a captura e o laudo já chamam, e
 * é o que permite às 4 telas escreverem só `<StepperVistoria etapa={n} />`.
 *
 * ## ⚠️ A exceção que o `13-ui-kit.md` manda conferir antes de passar `aoIrPara`
 *
 * Sair da captura com fotos capturadas e **não enviadas** PERDE trabalho — elas
 * não estão no banco. Quando a tela informa `fotosNaoEnviadas`, a navegação passa
 * por um `ModalConfirmacao` dizendo quantas serão descartadas. Nas outras três
 * telas não há esse risco (o que elas têm é gravado por requisição própria).
 */

interface PropsStepperVistoria {
  /** Etapa desta tela, de 1 a 4. */
  etapa: number;
  /**
   * Fotos capturadas nesta sessão e ainda **não** enviadas ao banco.
   *
   * Só a captura tem isso. Serve para confirmar antes de descartar — ver o aviso
   * acima.
   */
  fotosNaoEnviadas?: number;
  /**
   * A vistoria **já lida do banco** pela tela — quando informada, o componente
   * NÃO faz a própria leitura (at-64).
   *
   * 🔴 Existe por causa do **laudo**, e o motivo não é economizar um GET: é ter
   * **uma verdade só**. Aquela tela precisa da leitura para outras coisas
   * (`envioSga`, a contagem "N de M fotos", as travas de finalizar) e a **relê**
   * depois de gerar o PDF e depois de voltar de uma correção. Com duas leituras
   * independentes, a lista textual de bloqueios e as bolinhas podem discordar
   * sobre a mesma vistoria por um instante — que é exatamente o tipo de
   * divergência que a convenção da at-24 manda evitar.
   *
   * ⚠️ Ausente = comportamento das outras três telas: o componente lê sozinho.
   * `null` explícito significa "li e não existe vistoria", diferente de `undefined`.
   */
  vistoria?: VistoriaNoBanco | null;
  /** Acompanha `vistoria`: a leitura da tela falhou. Só vale com `vistoria` presente. */
  leituraFalhou?: boolean;
}

const StepperVistoria: React.FC<PropsStepperVistoria> = ({
  etapa,
  fotosNaoEnviadas = 0,
  vistoria: vistoriaExterna,
  leituraFalhou: leituraExternaFalhou,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  /*
   * Fatias pontuais, não o store inteiro: com `(state) => state` o componente
   * re-renderizaria a cada mudança de qualquer slice do app.
   */
  const vistoriaId = useSelector((state: RootState) => state.vistoria.vistoriaId);
  const tipoVeiculo = useSelector((state: RootState) => state.vistoria.vehicle?.tipoVeiculo);
  const associadoId = useSelector((state: RootState) => state.associado.associadoId);
  const carroId = useSelector((state: RootState) => state.vistoria.vehicle?.id);

  /**
   * Aberta pelo card "Editar" do laudo (at-24)? Então a navegação pelas bolinhas
   * **preserva** essa marca: é ela que faz cada etapa voltar ao laudo ao salvar,
   * em vez de arrastar o consultor pelo fluxo inteiro e forçar uma NOVA
   * assinatura. Se a tela não veio de lá, não se inventa a marca.
   */
  const origemEdicao = (location.state as any)?.origemEdicao === true;

  const [doBanco, setDoBanco] = useState<VistoriaPorIdResponse | null>(null);
  /** `true` só quando a requisição REALMENTE falhou — não durante a espera. */
  const [leituraFalhou, setLeituraFalhou] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [destinoConfirmar, setDestinoConfirmar] = useState<number | null>(null);

  /** A tela nos deu a leitura dela? Então não fazemos a nossa. */
  const fonteExterna = vistoriaExterna !== undefined;

  useEffect(() => {
    if (fonteExterna) return undefined;

    if (!vistoriaId) {
      setDoBanco(null);
      setLeituraFalhou(false);
      setCarregando(false);
      return undefined;
    }

    let ativo = true;
    setLeituraFalhou(false);
    setCarregando(true);

    buscarVistoriaPorId({ vistoriaId })
      .then((resposta) => {
        if (ativo) setDoBanco(resposta);
      })
      .catch(() => {
        // Nunca deixa bolinha muda: `leituraFalhou` vira motivo em texto.
        if (ativo) {
          setDoBanco(null);
          setLeituraFalhou(true);
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [vistoriaId, fonteExterna]);

  const estado: EstadoDaVistoria = fonteExterna
    ? {
        vistoria: vistoriaExterna ?? null,
        leituraFalhou: leituraExternaFalhou ?? false,
        tipoVeiculo,
      }
    : {
        vistoria: vistoriaId ? doBanco : null,
        leituraFalhou: vistoriaId ? leituraFalhou : false,
        tipoVeiculo,
      };

  const situacao = situacaoDasEtapasDaVistoria(estado, etapa);

  /*
   * ⚠️ **Carregando NÃO é o mesmo que falhou**, e o texto tem de dizer a verdade:
   * durante a espera, trava as outras etapas com "conferindo", em vez de acusar
   * uma falha que não houve. É a distinção que a at-53 fixou entre `EM_ANDAMENTO`
   * e `FALHOU` — só que aqui em miniatura.
   *
   * 📌 Enquanto isso, `pendentes` fica vazio: afirmar pendência sem ter lido o
   * banco é justamente o defeito que estas bolinhas dinâmicas vêm corrigir.
   */
  /* Com fonte externa não há espera nossa: a tela já resolveu (ou falhou). */
  const esperando = !fonteExterna && carregando;

  const pendentes = esperando ? [] : situacao.pendentes;
  const desabilitadas = esperando
    ? Object.fromEntries(
        [1, 2, 3, 4]
          .filter((numero) => numero !== etapa)
          .map((numero) => [numero, 'conferindo o estado desta vistoria']),
      )
    : situacao.desabilitadas;

  /**
   * Rotas FIXAS — diferente da adesão, que precisa saber qual dos três fluxos
   * está em curso para resolver as etapas 1 a 3 (`rotaDaEtapaDoFluxo`). A
   * vistoria tem um fluxo só.
   */
  const irPara = useCallback(
    (destino: number) => {
      const marca = origemEdicao ? { origemEdicao: true } : undefined;

      if (destino === 1) {
        navigate(VISTORIA_CAPTURA(), { state: marca });
        return;
      }
      if (destino === 2) {
        navigate(VISTORIA_CHECKLIST_ACESSORIOS(), { state: marca });
        return;
      }
      if (destino === 3) {
        // ✅ Constante desde a at-64: a rota deixou de ser `/assinatura-termo` e
        // virou `/vistoria/assinatura`. O `state` carrega o que aquela tela e a
        // `/carteira` já mandam.
        navigate(VISTORIA_ASSINATURA(), {
          state: { associadoId, carroId, ...(marca ?? {}) },
        });
        return;
      }
      navigate(VISTORIA_LAUDO(), { state: marca });
    },
    [navigate, origemEdicao, associadoId, carroId],
  );

  const aoIrPara = (destino: number) => {
    // Fotos ainda em memória: confirmar antes de descartá-las.
    if (fotosNaoEnviadas > 0) {
      setDestinoConfirmar(destino);
      return;
    }
    irPara(destino);
  };

  return (
    <>
      <Stepper
        etapa={etapa}
        rotulos={ETAPAS_VISTORIA}
        pendentes={pendentes}
        desabilitadas={desabilitadas}
        aoIrPara={aoIrPara}
      />

      <ModalConfirmacao
        aberto={destinoConfirmar !== null}
        titulo="Sair sem enviar as fotos?"
        mensagem={
          fotosNaoEnviadas === 1
            ? '1 foto capturada ainda não foi enviada.'
            : `${fotosNaoEnviadas} fotos capturadas ainda não foram enviadas.`
        }
        detalhe="Elas serão descartadas e você precisará capturá-las de novo. As fotos já enviadas ao servidor são preservadas."
        rotuloConfirmar="Sair e descartar"
        destrutivo
        onConfirmar={() => {
          const destino = destinoConfirmar;
          setDestinoConfirmar(null);
          if (destino !== null) irPara(destino);
        }}
        onCancelar={() => setDestinoConfirmar(null)}
      />
    </>
  );
};

export default StepperVistoria;
